import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getCurrentConference } from '../base/conference/functions';
import { CAPTION_PREFERENCES_STORAGE_KEY, getActiveCaptionSurface, getCaptionPreferences, isCaptionTranscriptRetentionAllowed, subscribeCaptionPreferences } from './captionPreferences';
import { getCaptionTonePresentation } from './captionTone';
import { formatSubtitleHistory } from './functions';
import logger from './logger';
import { formatCaptionTime, getCaptionLanguage, getLocalizedCaptionToneHint } from './captionLocalization';
/** Shared local-history and export ownership for web copy and native share. */
export function useCaptionTranscript() {
    const { t, i18n } = useTranslation();
    const language = getCaptionLanguage(i18n);
    const { subtitlesHistory: history } = useSelector((state) => state['features/subtitles']);
    const conference = useSelector(getCurrentConference);
    const [preferences, setPreferences] = useState(getCaptionPreferences);
    const transcriptRetentionAllowed = isCaptionTranscriptRetentionAllowed();
    const { keepTranscript } = preferences;
    const formatOptions = {
        language, unknownSpeaker: t('captionTranscript.unknownSpeaker'), interim: t('captionTranscript.interim'),
        showSpeakerNames: preferences.showSpeakerNames
    };
    const transcriptText = useMemo(() => keepTranscript ? formatSubtitleHistory(history, formatOptions) : '', [history, keepTranscript, language, t, preferences.showSpeakerNames]);
    const [exportStatus, setExportStatus] = useState('idle');
    const scopeRef = useRef({ conference, transcriptText, history, formatOptions });
    const mountedRef = useRef(false);
    const requestRef = useRef(0);
    const pendingRef = useRef(false);
    const resetTimerRef = useRef(null);
    scopeRef.current = { conference, transcriptText, history, formatOptions };
    useEffect(() => {
        const refresh = () => setPreferences(getCaptionPreferences());
        const unsubscribe = subscribeCaptionPreferences(refresh);
        const browserWindow = typeof window !== 'undefined' && typeof window.addEventListener === 'function'
            ? window : undefined;
        const onStorage = (event) => {
            if (event.key === null || event.key === CAPTION_PREFERENCES_STORAGE_KEY || event.key === 'vrs_user_info')
                refresh();
        };
        browserWindow?.addEventListener('storage', onStorage);
        refresh();
        return () => {
            unsubscribe();
            browserWindow?.removeEventListener('storage', onStorage);
        };
    }, []);
    useEffect(() => {
        mountedRef.current = true;
        setExportStatus('idle');
        return () => {
            mountedRef.current = false;
            requestRef.current++;
            pendingRef.current = false;
            if (resetTimerRef.current !== null)
                clearTimeout(resetTimerRef.current);
            resetTimerRef.current = null;
        };
    }, [conference, keepTranscript]);
    const exportTranscript = useCallback(async (exportText) => {
        if (!mountedRef.current || scopeRef.current.conference !== conference || pendingRef.current)
            return;
        // The settings storage can change while this dialog remains mounted.
        // Check at the user's action, rather than trusting its earlier render.
        const currentPreferences = getCaptionPreferences();
        if (!conference || !currentPreferences.keepTranscript || !scopeRef.current.transcriptText) {
            setExportStatus('unavailable');
            return;
        }
        const request = ++requestRef.current;
        const ownsRequest = () => mountedRef.current && requestRef.current === request
            && scopeRef.current.conference === conference;
        const updateStatus = (status) => {
            if (ownsRequest()) {
                setExportStatus(getCaptionPreferences().keepTranscript ? status : 'unavailable');
            }
        };
        pendingRef.current = true;
        if (resetTimerRef.current !== null)
            clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
        setExportStatus('pending');
        try {
            const text = formatSubtitleHistory(scopeRef.current.history, {
                ...scopeRef.current.formatOptions,
                showSpeakerNames: currentPreferences.showSpeakerNames
            });
            const completed = await exportText(text);
            if (ownsRequest()) {
                updateStatus(completed ? 'success' : 'idle');
                if (completed && getCaptionPreferences().keepTranscript) {
                    resetTimerRef.current = setTimeout(() => {
                        resetTimerRef.current = null;
                        updateStatus('idle');
                    }, 1500);
                }
            }
        }
        catch {
            logger.warn('Local caption transcript export failed.');
            updateStatus('error');
        }
        finally {
            if (requestRef.current === request)
                pendingRef.current = false;
        }
    }, [conference]);
    const toneLabel = (profile) => preferences.toneMarkerLane
        ? getLocalizedCaptionToneHint(getCaptionTonePresentation(profile, {
            mode: preferences.expressiveMode,
            surface: getActiveCaptionSurface()
        }), t)
        : undefined;
    return {
        canExport: Boolean(conference && transcriptText) && exportStatus !== 'pending',
        entries: keepTranscript ? history : [], exportStatus, exportTranscript,
        preferences, toneLabel, transcriptRetentionAllowed, t,
        formatTime: (timestamp) => formatCaptionTime(timestamp, language)
    };
}
